import { useTheme } from '@mui/material/styles';
import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import AddRecordButton from '@components/AddRecordButton';
import ContentLoader from '@components/ContentLoader';
import { CurationStatusBadge } from '@components/CurationStatusBadge';
import { TableStatus, TableNote } from '@components/DataTable';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import NullStateDiv from '@components/NullStateDiv';
import { RecordSourcePill } from '@components/RecordSourcePill';
import SelfReportedPill from '@components/SelfReportedPill/SelfReportedPill';
import {
  UpcomingAppointmentCard,
  UpcomingAppointmentGroup,
} from '@components/UpcomingAppointmentCard';

import * as ROUTES from '@constants/routes';

import { useGetAppointments } from '@hooks/api/appointment';
import { useIsMobile } from '@hooks/useIsMobile';
import { useSession } from '@hooks/useSession';

import { TableContainer, TableSpan } from '@layouts/Layouts/TableLayout';

import { splitRecordsByDate } from '@utils/format';
import { sortDates } from '@utils/sort/date';

import type { CurationStatus, ResourceSourceType } from 'generated/types';

const messages = defineMessages({
  noPast: {
    id: 'appointmentsMain.noPast',
    defaultMessage: 'NO KNOWN APPOINTMENT RECORDS',
  },
  past: {
    id: 'appointmentsMain.past',
    defaultMessage: 'Past Appointments',
  },
  upcoming: {
    id: 'appointmentsMain.upcoming',
    defaultMessage: 'Upcoming Appointments',
  },
  addUpcoming: {
    id: 'appointmentsMain.addUpcoming',
    defaultMessage: 'Add an upcoming appointment',
  },
  title: {
    id: 'appointmentsMain.title',
    defaultMessage: 'Appointments',
  },
  addRecord: {
    id: 'appointmentsMain.addRecord',
    defaultMessage: 'Add Appointment',
  },
});

const Appointments: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const isMobile = useIsMobile();
  const {
    state: { isClient, isPractitioner, isVitall },
  } = useSession();
  const { palette } = useTheme();

  const { data, loading: appointmentsLoading } = useGetAppointments();
  const loading = appointmentsLoading;

  const [pastAppointments, upcomingAppointments] = splitRecordsByDate(
    data,
    'getVisitDate'
  );

  const tableData = useMemo(
    () =>
      pastAppointments.map((a) => ({
        id: a.id,
        viewedStatus: a.viewedStatus,
        visitReason: a.visitReason,
        hasNotes: Boolean(a.recordNotes.length),
        facilityCity: a.facilityCity,
        hcpName: a.hcpName,
        facilityName: a.facilityName,
        appointmentTime: a.getVisitDateStr(),
        selfReported: a.selfReported,
        curationStatus: a.curationStatus,
        recordSource: a.recordSource,
      })),
    [pastAppointments]
  );

  return (
    <ContentLoader loading={loading}>
      <TableContainer>
        {upcomingAppointments.length > 0 && (
          <>
            <TableSpan>
              <h2>{formatMessage(messages.upcoming)}</h2>
            </TableSpan>
            <UpcomingAppointmentGroup>
              {upcomingAppointments
                .sort(({ visitDate: dateA }, { visitDate: dateB }) =>
                  sortDates('asc')(
                    { data: dateA ?? null },
                    { data: dateB ?? null }
                  )
                )
                .map((a) => (
                  <UpcomingAppointmentCard
                    colour={palette.secondary.main}
                    date={a.getVisitDate()}
                    key={`appointments-upcoming-${a.id}`}
                    onClick={() => {
                      navigate(`${ROUTES.APPOINTMENT_HISTORY}${a.id}`);
                    }}
                    subtitle={a.hcpName}
                    title={a.visitReason ?? '-'}
                  />
                ))}
            </UpcomingAppointmentGroup>
          </>
        )}

        {pastAppointments.length === 0 ? (
          <>
            <TableSpan>
              <h2>{formatMessage(messages.past)}</h2>
            </TableSpan>
            <NullStateDiv>{formatMessage(messages.noPast)}</NullStateDiv>
          </>
        ) : (
          <MuiDataTable
            columns={[
              {
                name: 'viewedStatus',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => {
                    return <TableStatus value={value} />;
                  },
                },
              },
              {
                name: 'curationStatus',
                label: 'Curation Status',
                className: 'small',
                options: {
                  customBodyRender: (status: CurationStatus) => (
                    <CurationStatusBadge status={status} />
                  ),
                  filter: isVitall,
                  filterType: 'multiselect',
                  display: isVitall,
                },
              },
              {
                name: 'recordSource',
                label: 'Record Source',
                className: 'small',
                options: {
                  customBodyRender: (source: ResourceSourceType) => (
                    <RecordSourcePill type={source} />
                  ),
                  filter: isVitall || isPractitioner,
                  filterType: 'multiselect',
                  display: isVitall || isPractitioner,
                },
              },
              {
                name: 'visitReason',
                className: 'blue-hover',
                label: 'Reason',
                options: {
                  filter: true,
                  filterType: 'multiselect',
                  searchable: true,
                },
              },
              {
                name: 'hasNotes',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => {
                    return <TableNote value={value} />;
                  },
                  display: !isMobile,
                },
              },
              {
                name: 'facilityCity',
                className: 'small-light',
                label: 'Facility City',
                options: {
                  filter: true,
                  filterType: 'multiselect',
                  searchable: true,
                },
              },
              {
                name: 'hcpName',
                className: 'small',
                label: 'Provider Name',
                options: {
                  filter: true,
                  filterType: 'multiselect',
                  searchable: true,
                },
              },
              {
                name: 'facilityName',
                className: 'small-light',
                label: 'Facility Name',
                options: {
                  filter: true,
                  filterType: 'multiselect',
                  searchable: true,
                },
              },
              {
                name: 'appointmentTime',
                className: 'date',
                label: 'Visit Date',
                options: {
                  filter: true,
                },
              },
              {
                name: 'selfReported',
                options: {
                  customHeadLabelRender: () => null,
                  customBodyRender: (value: boolean) => (
                    <SelfReportedPill value={value} />
                  ),
                },
              },
            ]}
            data={tableData}
            options={{
              sortOrder: {
                name: 'appointmentTime',
                direction: 'desc',
              },
              sort: true,
              search: true,
              filter: true,
              onRowClick: (_rowData, { dataIndex }) => {
                if (dataIndex <= tableData.length - 1) {
                  const row = tableData[dataIndex];
                  navigate(`${ROUTES.APPOINTMENT_HISTORY}${row.id}`);
                }
              },
            }}
            pageParam="appointments"
            title={formatMessage(messages.past)}
          />
        )}
        {isClient && (
          <AddRecordButton link={ROUTES.ADD_APPOINTMENT}>
            {formatMessage(messages.addRecord)}
          </AddRecordButton>
        )}
      </TableContainer>
    </ContentLoader>
  );
};

export default withErrorBoundary(Appointments, {
  FallbackComponent: ErrorBoundaryFallback,
});
